'use strict';

/**
 * @see app/common/components/fullscreen/FullscreenComponentBridge.ts in clinic-v4
 */
const COMPONENT_MESSAGE = {

  /**
   * Invoked by main iframe
   * Handled by clinic-v3
   * Sets the visibility of the fullscreen component.
   */
  SET_VISIBILITY: 'set-fullscreen-component-visibility',

  /**
   * Invoked by component iframe
   * Handled by main iframe
   * The fullscreen component has invoked a function prop.
   */
  INVOKE_FUNCTION_PROP: 'fullscreen-component-invoke-function-prop',

  /**
   * Invoked by main iframe
   * Handled by clinic-v3
   * The return value of a function prop invoked by the fullscreen component.
   */
  INVOKED_FUNCTION_PROP_RETURN: 'fullscreen-component-invoked-function-prop-return',

  /**
   * Invoked by the component iframe
   * Handled by clinic-v3
   * The fullscreen component is ready to receive props.
   */
  FULLSCREEN_COMPONENT_READY: 'fullscreen-component-ready',

  /**
   * Invoked by clinic-v3
   * Handled by the component iframe
   * Sends the fullscreen component its props.
   */
  SET_COMPONENT_PROPS: 'fullscreen-component-set-props',
};

angular
  .module('ClinicApp')
  .directive('ocFullscreenComponent', (
    $timeout,
    ClinicConfig,
    IframeService,
    OCDomUtil,
    GrowlService,
    OCLogUtil,
  ) => ({
    restrict: 'A',
    templateUrl: '/views/directives/oc-fullscreen-component.html',
    scope: {
      component: '=ocFullscreenComponent',
    },
    link: ($scope, $element) => {
      const { component } = $scope;
      const { componentId, instanceId } = component;

      const debug = ['staging', 'development'].includes(ClinicConfig.ENV);
      const debugLog = (...args) => {
        if (!debug) {
          return;
        }

        console.log('[Fullscreen Component - Clinic-v3]', ...args);
      };

      $scope.isVisible = false;
      let overlayScope = null;

      const getIframe = () => {
        const iframeElement = $element[0].querySelector(`#js-oc-fullscreen-component-iframe-${instanceId}`);
        if (!iframeElement) {
          GrowlService.error('An unknown error occurred. Please try again.');
          OCLogUtil.rollbar.error('Fullscreen component iframe not found', { instanceId, componentId });
        }

        return iframeElement;
      };

      $timeout(() => {
        const componentUrl = `${ClinicConfig.CLINIC_APP_V4_URL}/#/component/${componentId}?instance=${instanceId}`;
        getIframe().src = encodeURI(componentUrl);
      });

      const isForActiveComponent = (data) => data.instanceId === component.instanceId;

      const sendToComponent = (type, data) => {
        debugLog('Sending message to fullscreen component iframe', { type, data });

        IframeService.sendMessage({
          type,
          data,
          iframe: getIframe(),
        });
      };

      const sendToHost = (type, data) => {
        debugLog('Sending message to fullscreen component host', { type, data });

        IframeService.sendMessageToWindow({
          type,
          data,
          targetWindow: component.hostWindow,
        });
      };

      IframeService.onMessage(COMPONENT_MESSAGE.SET_VISIBILITY, (data) => {
        const { instanceId, isVisible } = data;
        if (!isForActiveComponent(data)) {
          return;
        }

        debugLog('Visibility change for fullscreen component', { instanceId, isVisible });

        $scope.isVisible = isVisible;

        overlayScope?.$destroy();
        if (isVisible) {
          overlayScope = $scope.$new();
          OCDomUtil.addOverlayBodyClass(overlayScope, true);
        }

        $scope.$applyAsync();
      });

      const setComponentProps = ({ props, callbackNames }) => {
        sendToComponent(COMPONENT_MESSAGE.SET_COMPONENT_PROPS, {
          instanceId,
          props,
          callbackNames,
        });
      };

      // The modal iframe has loaded and can receive its props
      IframeService.onMessage(COMPONENT_MESSAGE.FULLSCREEN_COMPONENT_READY, (data) => {
        if (!isForActiveComponent(data)) {
          return;
        }

        debugLog('Fullscreen component iframe is ready', { instanceId });

        setComponentProps({
          props: component.props,
          callbackNames: component.callbackNames,
        });
      });

      // The host has sent new props for the component
      IframeService.onMessage(COMPONENT_MESSAGE.SET_COMPONENT_PROPS, (data) => {
        if (!isForActiveComponent(data)) {
          return;
        }

        const { props } = data;
        debugLog('Fullscreen component iframe received new props', { instanceId, props });

        setComponentProps({
          props,
          callbackNames: component.callbackNames,
        });
      });

      // The component invoked one of the host's function props
      IframeService.onMessage(COMPONENT_MESSAGE.INVOKE_FUNCTION_PROP, (data) => {
        if (!isForActiveComponent(data)) {
          return;
        }

        debugLog(
          'Fullscreen component iframe invoked a callback',
          { instanceId, callId: data.callId, name: data.name },
        );

        sendToHost(COMPONENT_MESSAGE.INVOKE_FUNCTION_PROP, {
          instanceId: data.instanceId,
          callId: data.callId,
          name: data.name,
          args: data.args,
        });
      });

      // The host finished running a callback and is returning its result
      IframeService.onMessage(COMPONENT_MESSAGE.INVOKED_FUNCTION_PROP_RETURN, (data) => {
        if (!isForActiveComponent(data)) {
          return;
        }

        debugLog(
          'Fullscreen component iframe received a callback result',
          { instanceId, callId: data.callId, result: data.result, error: data.error },
        );

        sendToComponent(COMPONENT_MESSAGE.INVOKED_FUNCTION_PROP_RETURN, {
          callId: data.callId,
          result: data.result,
          error: data.error,
        });
      });

      $scope.$on('$destroy', () => {
        overlayScope?.$destroy();
      });
    },
  }));
